/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import {
  Navigate, Route, Routes, BrowserRouter,
} from 'react-router-dom';
import Cookies from 'js-cookie';
import WebFont from 'webfontloader';
import CssBaseline from '@mui/material/CssBaseline';
import Slide from '@mui/material/Slide';
import PropTypes from 'prop-types';
import { StylesProvider } from '@mui/styles';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import url from 'url';
import { isIE, isEdge, isEdgeChromium } from 'react-device-detect';
import { history } from './store/configureStore';
import flagNames from './store/featureFlags';
import NotSupportedBrowser from './modules/admissions/components/not-supported-browser/NotSupportedBrowser';
import Admin from './modules/admin/Admin';
import { checkSkipMaintenancePage } from './store/helpers/commonHelper';
import LoadingModal from './components/modal/LoadingModal';
import ScrollToTop from './components/scroll-to-top/ScrollToTop';
import Theme from './theme';
import SkipTo from './components/skip-to/SkipTo';

// Import auth-related components
import OidcReduxBridge from './components/auth/OidcReduxBridge';
import AuthenticatedRoute from './components/auth/AuthenticatedRoute';
import ClockSkewPreflight from './components/auth/ClockSkewPreflight';
import CallbackComponentWrapper from './components/auth/CallbackComponentWrapper';
import SuperPhoebeIntegration from './components/chat/SuperPhoebeIntegration';

// Lazy-loaded routes
const Admissions = lazy(() => import('./store/containers/admissions/AdmissionsContainer'));
const Maintenance = lazy(() => import('./store/containers/admissions/maintenance/MaintenanceContainer'));
const QuickApp = lazy(() => import('./store/containers/quickapp/QuickAppContainer'));
const LogoutContainer = lazy(() => import('./store/containers/logout/LogoutContainer'));

const basePath = window.__ENV.REACT_APP_BASE_PATH || '/application';

// Handle domain redirects if necessary
const rerouteFromBecome = () => {
  if (window.__ENV.REACT_APP_ENV !== 'local' && window.__ENV.REACT_APP_ENV !== 'dev') {
    let rerouteRequired = false;
    let newUrl = window.location.href;
    if (window.location.href.includes(window.__ENV.REACT_APP_BECOME_REDIRECT_FROM_DOMAIN)) {
      const wwwPrefix = window.__ENV.REACT_APP_ENV === 'production' && !window.location.href.includes('www.') ? 'www.' : '';
      newUrl = window.location.href.replace(
        window.__ENV.REACT_APP_BECOME_REDIRECT_FROM_DOMAIN,
        `${wwwPrefix}${window.__ENV.REACT_APP_BECOME_REDIRECT_TO_DOMAIN}`,
      );
      rerouteRequired = true;
    }
    if (!newUrl.includes(`${window.__ENV.REACT_APP_BECOME_REDIRECT_TO_DOMAIN}${basePath}`)) {
      newUrl = newUrl.replace(
        window.__ENV.REACT_APP_BECOME_REDIRECT_TO_DOMAIN,
        `${window.__ENV.REACT_APP_BECOME_REDIRECT_TO_DOMAIN}${basePath}`,
      );
      rerouteRequired = true;
    }
    if (rerouteRequired) {
      window.location.replace(newUrl);
      return true;
    }
  }
  return false;
};

const setSessionValuesFromQueryString = () => {
  const { query } = url.parse(window.location.href, true);
  if (query?.sagasCanaryDeployment) {
    sessionStorage.setItem('sagasCanaryDeployment', query.sagasCanaryDeployment);
  }
  if (query?.synthetic) {
    sessionStorage.setItem('synthetic', query.synthetic);
  }
};

const checkMaintenanceEnabled = () => {
  const skipMaintenance = checkSkipMaintenancePage();
  if (skipMaintenance) {
    return false;
  }
  if (window.__ENV.REACT_APP_FEATURE_FLAG_MAINTENANCE_PAGE_ENABLED === 'true') {
    sessionStorage.setItem('nae-maintenance-enabled', 'true');
    return true;
  }
  return sessionStorage.getItem('nae-maintenance-enabled') === 'true' || false;
};

const isUnsupportedBrowser = () => {
  let isUnsupported = false;

  if (isIE) {
    isUnsupported = true;
  }

  if (isEdge && !isEdgeChromium) {
    isUnsupported = true;
  }

  return isUnsupported;
};

// Only run once when page is loaded as browser version won't change.
const isUserBrowserUnsupported = isUnsupportedBrowser();
const isMaintenanceEnabled = checkMaintenanceEnabled();

// Check if we need to redirect from root URL to the application path
const handleRootUrlRedirect = () => {
  const currentPath = window.location.pathname;
  if (currentPath === '/' || currentPath === '') {
    window.location.replace(`${window.location.origin}${basePath}/quick-app`);
    return true;
  }
  return false;
};

const App = ({ featureToggles }) => {
  const rerouteRequired = rerouteFromBecome() || handleRootUrlRedirect();

  let mcIdValue = Cookies.get('s_ecid');
  if (mcIdValue) {
    const parts = mcIdValue.split('|');
    mcIdValue = parts.length > 1 && parts[0] === 'MCMID' ? parts[1] : '';
  }
  const cookie = document.cookie.match('(^|;) *AMCV_8DF667C25245B0070A490D4C%40AdobeOrg=([^;]*)');
  let value;
  let mcid;
  if (cookie) {
    value = decodeURI(cookie[2]);
    value = value.substring(value.lastIndexOf('|MCMID|') + 7);
    mcid = value.substring(0, value.indexOf('|'));
  }
  sessionStorage.setItem('mcId', mcIdValue || mcid || '');

  setSessionValuesFromQueryString();

  const loadingModal = <LoadingModal />;

  useEffect(() => {
    WebFont.load({
      google: {
        families: ['Roboto:300,400,500,700,900', 'IBM Plex Serif:300,400,500,700'],
      },
    });
  }, []);

  return rerouteRequired ? loadingModal : (
    <StyledEngineProvider injectFirst>
      <StylesProvider injectFirst>
        <ThemeProvider theme={Theme}>
          <CssBaseline />
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            TransitionComponent={Slide}
          >
            {/* OIDC-Redux state synchronization */}
            <OidcReduxBridge />
            <SuperPhoebeIntegration />

            <BrowserRouter basename={basePath}>
              <SkipTo />
              <ScrollToTop />
              {isUserBrowserUnsupported ? <NotSupportedBrowser /> : (
                <Suspense fallback={loadingModal}>
                  <Routes>
                    <Route path="/admin/maintenance" element={<Admin maintenance />} />
                    <Route path="/admin" element={<Admin />} />

                    {isMaintenanceEnabled && (
                      <Route path="/maintenance" element={<Maintenance />} />
                    )}

                    <Route
                      path="/quick-app/*"
                      element={(
                        <QuickApp
                          forceLogoutEnabled={featureToggles[flagNames.QUICKAPP_FORCE_LOGOUT]}
                          featureToggles={featureToggles}
                          flagNames={flagNames}
                        />
                      )}
                    />

                    <Route
                      path="/onboarding"
                      element={<Navigate to={window.__ENV.REACT_APP_MYPHOENIX_HOME_URL} replace />}
                    />

                    <Route
                      path="/admissions/*"
                      element={(
                        <ClockSkewPreflight includeProtectedPaths>
                          <AuthenticatedRoute>
                            <Admissions
                              featureToggles={featureToggles}
                              flagNames={flagNames}
                            />
                          </AuthenticatedRoute>
                        </ClockSkewPreflight>
                      )}
                    />

                    <Route path="/authdepot/callback/*" element={<CallbackComponentWrapper />} />
                    <Route path="/oidc/callback" element={<CallbackComponentWrapper />} />

                    <Route path="/logout" element={<LogoutContainer />} />
                    <Route path="*" element={<Navigate to="/quick-app" />} />
                  </Routes>
                </Suspense>
              )}
            </BrowserRouter>
          </SnackbarProvider>
        </ThemeProvider>
      </StylesProvider>
    </StyledEngineProvider>
  );
};

App.propTypes = {
  featureToggles: PropTypes.shape({}).isRequired,
};

export default App;
